import TF from "@/components/forms/TextField.module.css";

import { FormField } from "@/components/forms/FormField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { EditableStringList } from "@/modules/network/firewall/EditableList.tsx";
import { modifyFirewallMutation } from "@/modules/network/firewall/firewallApi.ts";
import { endPortLabel, startPortLabel } from "@/modules/network/firewall/firewallLabels.tsx";
import { getRuleType, ruleEndpointSpecs, type RuleType, ruleTypes } from "@/modules/network/firewall/ruleSpec.tsx";
import { validateEndPort } from "@/modules/network/firewall/validatePorts.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
    Firewall,
    FirewallRule,
    FirewallRuleDirectionType,
    FirewallRuleEndpointSpecType,
    FirewallRuleProtocolType,
} from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import type { ReactNode } from "react";
import { Input } from "react-aria-components";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

interface RuleFormType {
    firewall: Firewall;
    rule: FirewallRule;
    location: string;
    direction: FirewallRuleDirectionType;
    isEditMode?: boolean;
}

interface EditRuleType extends Omit<RuleFormType, "direction"> {
    button: ReactNode;
}

interface AddRuleType extends Omit<RuleFormType, "rule"> {
    button: ReactNode;
}

interface EditRuleInputs {
    type: RuleType;
    protocol: FirewallRuleProtocolType;
    port_start: number;
    port_end: number | undefined;
    endpoint_spec_type: FirewallRuleEndpointSpecType;
    endpoint_spec: string[];
}

export function AddRuleButton({ firewall, location, button, direction }: AddRuleType) {
    const defaultType = ruleTypes[0];
    const rule: FirewallRule = {
        direction,
        protocol: defaultType.protocol[0],
        port_start: defaultType.port_start,
        port_end: defaultType.port_end,
        endpoint_spec_type: "any",
        endpoint_spec: [],
    };

    return (
        <WModal button={button}>
            <EditRuleModalContent location={location} firewall={firewall} rule={rule} direction={direction} />
        </WModal>
    );
}

export function EditRuleButton({ firewall, rule, location, button }: EditRuleType) {
    return (
        <WModal button={button}>
            <EditRuleModalContent
                location={location}
                firewall={firewall}
                rule={rule}
                direction={rule.direction}
                isEditMode
            />
        </WModal>
    );
}

function EditRuleModalContent({ firewall, rule, location, direction, isEditMode }: RuleFormType) {
    //#region Hooks
    const { t } = useLingui();
    const modifyMutation = useStandardMutation(modifyFirewallMutation);
    const selectedType = notNull(ruleTypes.find((item) => item.title === getRuleType(rule)));
    //#endregion

    const onSubmit: SubmitHandler<EditRuleInputs> = async (inputs) => {
        const { protocol, port_start, port_end, endpoint_spec_type, endpoint_spec } = inputs;

        const updatedRules = [...firewall.rules];
        const updatedRule = {
            protocol,
            port_start,
            port_end,
            endpoint_spec_type,
            endpoint_spec,
            direction,
        };

        const index = updatedRules.findIndex((item) => item.uuid === rule.uuid);

        if (index !== -1) {
            Object.assign(updatedRules[index], updatedRule);
        } else {
            updatedRules.push(updatedRule);
        }

        await modifyMutation.mutateAsync({
            location,
            uuid: firewall.uuid,
            body: { rules: updatedRules },
        });
    };

    const form = useForm<EditRuleInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            type: selectedType,
            protocol: rule.protocol,
            port_start: rule.port_start ?? undefined,
            port_end: rule.port_end ?? undefined,
            endpoint_spec_type: rule.endpoint_spec_type,
            endpoint_spec: rule.endpoint_spec ?? undefined,
        },
    });

    const { control, setValue, getValues, watch, formState, clearErrors } = form;

    return (
        <WModalForm
            title={isEditMode ? <Trans>Edit rule</Trans> : <Trans>Add rule</Trans>}
            label={isEditMode ? <Trans>Edit</Trans> : <Trans>Add</Trans>}
            form={form}
            onSubmit={onSubmit}
            className="w-24rem"
        >
            <FormField wide headless isRequired label={t`Type`}>
                <Controller
                    control={control}
                    name="type"
                    {...controllerProps({ isRequired: true })}
                    render={({ field }) => (
                        <WSelect
                            aria-label="Select type"
                            items={ruleTypes}
                            getKey={(item) => item.type}
                            getTextValue={(item) => item.type}
                            searchItems
                            value={field.value}
                            onChange={(v) => {
                                field.onChange(v);

                                clearErrors("port_end");

                                setValue("protocol", v.protocol[0]);
                                if (v.type !== "Custom") {
                                    setValue("port_start", v.port_start);
                                    setValue("port_end", v.port_end);
                                } else {
                                    if (!getValues("port_start")) setValue("port_start", v.port_start);
                                }
                            }}
                        >
                            {(item) => item.title}
                        </WSelect>
                    )}
                />
            </FormField>

            <FormField wide headless isRequired label={t`Protocol`}>
                <Controller
                    control={control}
                    name="protocol"
                    {...controllerProps({ isRequired: true })}
                    render={(props) => (
                        <WSelect
                            aria-label="Select protocol"
                            items={watch("type.protocol")}
                            getKey={(item) => item}
                            getTextValue={(item) => item}
                            searchItems
                            {...formPropsToAriaProps(props)}
                            isDisabled={watch("type.protocol").length < 2}
                        >
                            {(item) => (item === "any" ? <Trans>Any</Trans> : item.toUpperCase())}
                        </WSelect>
                    )}
                />
            </FormField>

            {watch("protocol") === "any" || watch("protocol") === "icmp" || watch("protocol") === "icmp6" ? null : (
                <Controller
                    control={control}
                    name="port_start"
                    {...controllerProps({ isRequired: true })}
                    render={(props) => (
                        <WNumberField
                            label={startPortLabel}
                            headless
                            wide
                            className={cn(TF.NumberField)}
                            minValue={1}
                            maxValue={65535}
                            aria-label="Set port start"
                            formatOptions={{
                                maximumFractionDigits: 0,
                                signDisplay: "never",
                                useGrouping: false,
                            }}
                            {...formPropsToAriaProps(props)}
                            isDisabled={!watch("type.isCustom")}
                        >
                            <Input className={TF.Input} />
                        </WNumberField>
                    )}
                />
            )}

            {(watch("type.isCustom") ?? watch("type.port_end")) && (
                <Controller
                    control={control}
                    name="port_end"
                    rules={{
                        validate: (v, f) => {
                            return !f.port_start || validateEndPort(v, f.port_start);
                            // return f.type.type === "Custom" && (!f.port_start || validateEndPort(v, f.port_start));
                        },
                    }}
                    render={(props) => (
                        <WNumberField
                            label={endPortLabel}
                            headless
                            wide
                            className={cn(TF.NumberField)}
                            minValue={1}
                            maxValue={65535}
                            aria-label="Set port end"
                            formatOptions={{
                                maximumFractionDigits: 0,
                                signDisplay: "never",
                                useGrouping: false,
                            }}
                            {...formPropsToAriaProps(props)}
                            isDisabled={!watch("type.isCustom")}
                        >
                            <Input className={TF.Input} />
                        </WNumberField>
                    )}
                />
            )}

            <FormField
                headless
                block
                isRequired
                label={rule.direction === "outbound" ? <Trans>Destinations</Trans> : <Trans>Sources</Trans>}
            >
                <div className="pb-2">
                    <Controller
                        control={control}
                        name="endpoint_spec_type"
                        {...controllerProps({ isRequired: true })}
                        render={({ field }) => (
                            <WSelect
                                aria-label="Select spec"
                                items={ruleEndpointSpecs}
                                getKey={(item) => item.type}
                                getTextValue={(item) => item.type}
                                searchItems
                                valueKey={field.value}
                                onChange={(v) => {
                                    field.onChange(v.type);
                                    if (v.type === "any") setValue("endpoint_spec", []);
                                }}
                            >
                                {(item) => item.title}
                            </WSelect>
                        )}
                    />
                </div>

                <div>
                    <Controller
                        control={control}
                        name="endpoint_spec"
                        {...controllerProps({ isRequired: watch("endpoint_spec_type") !== "any" })}
                        render={({ field }) => (
                            <EditableStringList
                                isDisabled={watch("endpoint_spec_type") === "any"}
                                items={field.value}
                                onChange={field.onChange}
                                errorMessage={
                                    formState.isSubmitted &&
                                    watch("endpoint_spec_type") !== "any" &&
                                    !watch("endpoint_spec").length ? (
                                        <Trans>At least one IP is required</Trans>
                                    ) : undefined
                                }
                            />
                        )}
                    />
                </div>
            </FormField>
        </WModalForm>
    );
}
